<template>
  <AsidePanel
    ref="asidePanel"
    cancelText="close"
    :components="components"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="ldap"
      />
    </template>
  </AsidePanel>
</template>
<script>
import * as Constants from 'client/util/Constants';
import { featureFlags } from 'client/store/modules/FeatureFlagsModule';
import { isEqual } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LdapDetails from 'client/directories/Ldap/LdapDetails.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import RbacUtils from 'client/util/rbacUtils';
import RouteGuardMixin from 'client/AdminPortal/router/guards/RouteGuardMixin';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemUserItemTable from 'client/systemusers/SystemUserItemTable/SystemUserItemTable.vue';
import SystemUserItemTableColumns from 'client/systemusers/SystemUserItemTable/SystemUserItemTableColumns';
import UserGroupItemTable from 'client/components/ItemTable/UserGroupItemTable.vue';

const {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  LdapCustomAttributesConsts,
  StoreModelMap,
  StoreModuleMap,
} = Constants;

const { create, update } = ItemActionConsts;
const {
  directory,
  ldap,
  settings,
  systemUser,
  userGroup,
} = ItemTypeConsts;
const { ldapCustomAttributes } = featureFlags;
const directoryCollection = StoreModuleMap[directory];
const ldapModel = StoreModelMap[ldap];
const userGroupCollection = StoreModuleMap[userGroup];
const settingsModule = StoreModuleMap[settings];

export default {
  name: 'LdapAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
  },

  mixins: [
    AsidePanelPropsMixin,
    RouteGuardMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: ldap,
      required: false,
    },
  },

  data() {
    // Getters are used for the header labels because the displayName isn't available at the time
    // this component's data is defined.
    const userGroupHeaderLabel = () => `Members of the following user groups are syncing to the ${this.displayName} Instance`;
    const userHeaderLabel = () => `The following users are syncing to the ${this.displayName} Instance`;

    const components = [{
      label: 'Details',
      vueComponent: LdapDetails,
    }, {
      itemType: userGroup,
      label: 'User Groups',
      headerLabels: {
        get [create]() {
          return userGroupHeaderLabel();
        },
        get [update]() {
          return userGroupHeaderLabel();
        },
        useDisplayName: false,
      },
      vueComponent: UserGroupItemTable,
      vueComponentProps: {
        boundItemsUrl: `/api/v2/ldapservers/${this.itemId}/usergroups`,
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.directoryAsidePanel,
      },
    }, {
      itemType: systemUser,
      label: 'Users',
      headerLabels: {
        get [create]() {
          return userHeaderLabel();
        },
        get [update]() {
          return userHeaderLabel();
        },
        useDisplayName: false,
      },
      vueComponent: SystemUserItemTable,
      vueComponentProps: {
        boundItemsUrl: `/api/v2/ldapservers/${this.itemId}/users`,
        columns: [
          SystemUserItemTableColumns.passwordStatus,
          SystemUserItemTableColumns.name,
          SystemUserItemTableColumns.email,
          SystemUserItemTableColumns.bindStatus,
        ],
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.directoryAsidePanel,
      },
    }];

    return {
      components,
      error403message: RbacUtils.getForbiddenMessage(),
      itemHasBeenSaved: false,
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information',
    };
  },

  computed: {
    ...mapGetters(ldapModel, [
      'displayName',
      'getLdapCustomAttributes',
      'getPreviousLdapCustomAttributes',
      'hasItemChanged',
      'haveAssociationsChanged',
      'haveLdapBindDnsChanged',
    ]),
    ...mapGetters(settingsModule, ['isAdmin']),
    ...mapState('FeatureFlagsModule', {
      ldapAttributesFeatureFlag: state => state.betaFeatures[ldapCustomAttributes],
    }),
  },

  methods: {
    ...mapActions(directoryCollection, [
      'refreshCollection',
    ]),

    ...mapActions(ldapModel, [
      'bindAssociations',
      'deleteLdapCustomAttributes',
      'saveItem',
      'saveLdapBindDns',
      'saveLdapCustomAttributes',
      'updateUserGroupsSambaAuthentication',
    ]),

    ...mapGetters(userGroupCollection, [
      'getSambaEnabledGroupsToUpdate',
    ]),

    confirmEnableSambaForUserGroups() {
      return new Promise((resolve) => {
        const componentProps = {
          mdTemplate: 'By enabling User Groups to be authenticated to your Samba server\'s file share, JumpCloud must generate and store an MD4 password hash, a requirement for NTLM-based authentication.',
          cancelText: 'cancel',
          continueText: 'Enable',
          handleCancel: () => {
            resolve(false);
            ModalEventBus.close();
          },
          handleContinue: () => {
            resolve(true);
            ModalEventBus.close();
          },
          headerText: 'Security Warning',
          kbLink: KbArticleUrls.enablingSambaSupportWithLdap,
          showClose: true,
        };

        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
        });
      });
    },

    async createLdapCustomAttributes() {
      const attributes = Object.values(this.getLdapCustomAttributes);
      const previousAttributes = Object.values(this.getPreviousLdapCustomAttributes);
      await Promise.all(attributes.map(async (attribute, index) => {
        const { value } = attribute;
        const previousValue = previousAttributes[index].value;

        if (value
          && value !== LdapCustomAttributesConsts.None
          && previousValue !== value
        ) {
          const rule = {
            external: {
              type: 'path',
              value: `jcAttribute${index + 1}`,
            },
            internal: {
              type: 'path',
              value,
            },
            direction: 'export',
          };
          try {
            await this.saveLdapCustomAttributes(rule);
          } catch (error) {
            notify(error, 'There was a problem saving LDAP custom attributes. Please try again.');
          }
        }
      }));
    },

    async handleSave() {
      // Note that because no changes are actually _saved_ to LDAP itself, the
      // admin's role is manually checked here so that admins who cannot save
      // changes will be notified of this when they try to save changes to LDAP.
      if (!this.isAdmin) {
        throw new Error(this.error403message);
      }

      await this.$validator.validate();

      if (this.$validator.errors.any()) {
        throw Error(this.validationErrorMessage);
      }

      const enabledGroupsToUpdate = this.getSambaEnabledGroupsToUpdate();
      let canContinue = true;

      if (enabledGroupsToUpdate.length > 0) {
        canContinue = await this.confirmEnableSambaForUserGroups();
      }

      if (canContinue === false) {
        return new SaveReturnValue({ canContinue });
      }

      this.itemHasBeenSaved = true;
      const parentType = ldap;

      try {
        await this.saveItem();
        await this.bindAssociations({ parentType, targetType: userGroup });
        await this.bindAssociations({ parentType, targetType: systemUser });
        await this.saveLdapBindDns();
        await this.updateUserGroupsSambaAuthentication();
        if (this.ldapAttributesFeatureFlag && this.getLdapCustomAttributes) {
          await this.saveLdapAttributes();
        }
        this.refreshCollection();
        return { canContinue };
      } catch (err) {
        throw err instanceof Error ? err : Error('Unknown error');
      }
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.hasItemChanged
        || this.haveAssociationsChanged([userGroup, systemUser])
        || this.haveLdapBindDnsChanged
        || this.haveLdapAttributesChanged();
    },

    /**
     * @todo Consider moving some aspect of this method to the AsidePanel component. This is
     * currently duplicated in all AsidePanel parent components.
     */

    haveLdapAttributesChanged() {
      if (!this.ldapAttributesFeatureFlag || !this.getLdapCustomAttributes) {
        return false;
      }

      const attributes = Object.values(this.getLdapCustomAttributes);
      const previousAttributes = Object.values(this.getPreviousLdapCustomAttributes);

      return !isEqual(attributes, previousAttributes);
    },

    async removeLdapCustomAttributes() {
      const attributes = Object.values(this.getLdapCustomAttributes);
      const previousAttributes = Object.values(this.getPreviousLdapCustomAttributes);
      await Promise.all(attributes.map(async (attribute, index) => {
        const { value } = attribute;
        const previousValue = previousAttributes[index].value;
        const { id } = attribute;

        if (id && previousValue !== value) {
          try {
            await this.deleteLdapCustomAttributes({ attributeId: id });
          } catch (error) {
            notify(error, 'There was a problem deleting LDAP custom attributes. Please try again.');
          }
        }
      }));
    },

    async saveLdapAttributes() {
      await this.removeLdapCustomAttributes();
      await this.createLdapCustomAttributes();
    },
  },
};
</script>
